/**
 * Analytics and Calculations Module
 * Computes summaries, aggregations, and statistics from transactions
 */

import Fuse from 'fuse.js';
import type {
  Transaction,
  CategorySummary,
  PersonSummary,
  MonthSummary,
  VendorSummary,
  Summary,
  FilterCriteria,
  SortField,
  SortOrder,
  TopCategory
} from './types';

// Re-export types for convenience
export type {
  Transaction,
  CategorySummary,
  PersonSummary,
  MonthSummary,
  VendorSummary,
  Summary,
  FilterCriteria,
  SortField,
  SortOrder,
  TopCategory
};

// ============================================================================
// Constants
// ============================================================================

const EXCLUDED_CATEGORIES = ['transfer', 'credit card payment'] as const;

/**
 * Income categories - transactions in these should have positive amounts
 * These are used to distinguish true income from refunds/credits
 */
const INCOME_CATEGORIES = [
  'salary & wages',
  'interest & dividends',
  'income - other',
  'bonus & windfalls',
  'tax return',
  'income',
] as const;

/**
 * Savings/Investment categories - tracked separately from expenses
 * These represent transfers of wealth (savings) rather than consumption (expenses)
 */
const SAVINGS_CATEGORIES = [
  'investment',
  '529',
  'retirement',
  'savings',
] as const;

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Check if a category should be excluded from analytics
 */
function isExcludedCategory(category: string): boolean {
  return EXCLUDED_CATEGORIES.includes(category.toLowerCase() as any);
}

/**
 * Check if a category is an income category
 * Used to distinguish true income from refunds/credits (positive amounts in expense categories)
 */
function isIncomeCategory(category: string): boolean {
  return INCOME_CATEGORIES.includes(category.toLowerCase() as any);
}

/**
 * Check if a category is a savings/investment category
 * These are tracked separately from expenses (savings, not consumption)
 */
function isSavingsCategory(category: string): boolean {
  return SAVINGS_CATEGORIES.includes(category.toLowerCase() as any);
}

/**
 * Check if a transaction should be included in analytics
 */
function shouldIncludeInAnalytics(
  transaction: Transaction,
  includeExcluded: boolean = false
): boolean {
  if (!includeExcluded && transaction.excluded) {
    return false;
  }
  return true;
}

/**
 * Get vendor name from transaction (prefers displayName)
 */
function getVendorName(transaction: Transaction): string {
  return transaction.displayName || transaction.description || 'Unknown';
}

/**
 * Format month key from date string
 * Extracts YYYY-MM directly from date string to avoid timezone issues
 */
function getMonthKey(date: Date | string): string {
  if (typeof date === 'string') {
    // Extract YYYY-MM directly from 'YYYY-MM-DD' format
    return date.substring(0, 7);
  }
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Initialize category summary if not exists
 */
function initCategorySummary(
  byCategory: Record<string, CategorySummary>,
  category: string
): CategorySummary {
  if (!byCategory[category]) {
    byCategory[category] = { count: 0, amount: 0 };
  }
  return byCategory[category];
}

/**
 * Initialize person summary if not exists
 */
function initPersonSummary(
  byPerson: Record<string, PersonSummary>,
  person: string
): PersonSummary {
  if (!byPerson[person]) {
    byPerson[person] = { count: 0, income: 0, expenses: 0 };
  }
  return byPerson[person];
}

/**
 * Initialize month summary if not exists
 */
function initMonthSummary(
  byMonth: Record<string, MonthSummary>,
  monthKey: string
): MonthSummary {
  if (!byMonth[monthKey]) {
    byMonth[monthKey] = { income: 0, expenses: 0, count: 0 };
  }
  return byMonth[monthKey];
}

/**
 * Initialize vendor summary if not exists
 */
function initVendorSummary(
  byVendor: Record<string, VendorSummary>,
  vendor: string
): VendorSummary {
  if (!byVendor[vendor]) {
    byVendor[vendor] = { count: 0, amount: 0 };
  }
  return byVendor[vendor];
}

// ============================================================================
// Summary Calculation Functions
// ============================================================================

/**
 * Process income and expenses for a transaction
 *
 * REFUND HANDLING:
 * - Positive amounts in income categories → count as income
 * - Positive amounts in expense/savings categories (refunds) → EXCLUDED from totals (like transfers)
 * - Negative amounts in savings categories → count as savings (not expenses)
 * - Negative amounts in other categories → count as expenses
 *
 * Refunds are excluded from totals to prevent:
 * 1. Inflating income (the original problem)
 * 2. Creating negative expenses when filtering (e.g., showing only refunds)
 * 3. Confusion about whether refunds offset expenses or count as income
 *
 * Refunds remain visible in transaction lists but don't affect dashboard totals.
 */
function processIncomeExpenses(
  summary: Summary,
  transaction: Transaction,
  isExcluded: boolean
): void {
  const amount = transaction.amount || 0;
  const category = transaction.category || 'Uncategorized';

  if (!isExcluded) {
    if (amount > 0) {
      // Positive amount - check if it's true income or a refund
      if (isIncomeCategory(category)) {
        // True income (salary, wages, interest, etc.)
        summary.totalIncome += amount;
      }
      // Refunds (positive amounts in expense/savings categories) are excluded from totals
      // They don't count as income, and they don't reduce expenses
      // This is the cleanest approach and prevents negative totals when filtering
    } else {
      // Negative amount - check if savings or expense
      if (isSavingsCategory(category)) {
        // Investment/529/Retirement contribution - track as savings
        summary.totalSavings += Math.abs(amount);
      } else {
        // Regular expense
        summary.totalExpenses += Math.abs(amount);
      }
    }
  }
}

/**
 * Process category aggregation for a transaction
 */
function processCategoryAggregation(
  summary: Summary,
  transaction: Transaction,
  isExcluded: boolean
): void {
  const category = transaction.category || 'Uncategorized';
  const amount = transaction.amount || 0;

  // Exclude income, transfers, payments, and savings/investment from spending categories
  const categoryLower = category.toLowerCase();
  const isIncome = categoryLower === 'income' ||
                   categoryLower === 'salary & wages' ||
                   categoryLower === 'interest & dividends' ||
                   categoryLower === 'income - other' ||
                   categoryLower === 'bonus & windfalls' ||
                   categoryLower === 'tax return';

  // Also exclude categories like transfers and CC payments
  const isExcludedCat = isExcludedCategory(category);

  // Exclude savings/investment categories from expense charts
  const isSavings = isSavingsCategory(category);

  if (!isExcluded && !isIncome && !isExcludedCat && !isSavings) {
    const categorySummary = initCategorySummary(summary.byCategory, category);
    categorySummary.count++;
    categorySummary.amount += Math.abs(amount);
  }
}

/**
 * Process person aggregation for a transaction
 */
function processPersonAggregation(
  summary: Summary,
  transaction: Transaction,
  isExcluded: boolean
): void {
  if (isExcluded) return;

  const person = transaction.person || 'Unknown';
  const amount = transaction.amount || 0;
  const personSummary = initPersonSummary(summary.byPerson, person);

  personSummary.count++;
  if (amount > 0) {
    personSummary.income += amount;
  } else {
    personSummary.expenses += Math.abs(amount);
  }
}

/**
 * Process monthly aggregation for a transaction
 */
function processMonthAggregation(
  summary: Summary,
  transaction: Transaction,
  isExcluded: boolean
): void {
  if (isExcluded) return;

  const monthKey = getMonthKey(transaction.date);
  const amount = transaction.amount || 0;
  const monthSummary = initMonthSummary(summary.byMonth, monthKey);

  monthSummary.count++;
  if (amount > 0) {
    monthSummary.income += amount;
  } else {
    monthSummary.expenses += Math.abs(amount);
  }
}

/**
 * Process vendor aggregation for a transaction
 */
function processVendorAggregation(
  summary: Summary,
  transaction: Transaction,
  isExcluded: boolean
): void {
  const amount = transaction.amount || 0;
  const category = transaction.category || 'Uncategorized';

  // Only track expenses, exclude savings/investment transactions
  const isSavings = isSavingsCategory(category);

  if (!isExcluded && !isSavings && amount < 0) {
    const vendor = getVendorName(transaction);
    const vendorSummary = initVendorSummary(summary.byVendor, vendor);

    vendorSummary.count++;
    vendorSummary.amount += Math.abs(amount);
  }
}

/**
 * Calculate overall summary statistics
 */
export function calculateSummary(
  transactions: Transaction[],
  includeExcluded: boolean = false
): Summary {
  // Filter out excluded transactions unless explicitly requested
  const activeTransactions = transactions.filter(t =>
    shouldIncludeInAnalytics(t, includeExcluded)
  );

  const summary: Summary = {
    totalTransactions: activeTransactions.length,
    totalIncome: 0,
    totalExpenses: 0,
    totalSavings: 0,
    netBalance: 0,
    byCategory: {},
    byPerson: {},
    byMonth: {},
    byVendor: {}
  };

  activeTransactions.forEach(transaction => {
    const category = transaction.category || 'Uncategorized';
    const isExcluded = isExcludedCategory(category);

    processIncomeExpenses(summary, transaction, isExcluded);
    processCategoryAggregation(summary, transaction, isExcluded);
    processPersonAggregation(summary, transaction, isExcluded);
    processMonthAggregation(summary, transaction, isExcluded);
    processVendorAggregation(summary, transaction, isExcluded);
  });

  summary.netBalance = summary.totalIncome - summary.totalExpenses;

  return summary;
}

// ============================================================================
// Filtering Functions
// ============================================================================

/**
 * Check if transaction matches date range
 */
function matchesDateRange(
  transaction: Transaction,
  startDate?: string,
  endDate?: string
): boolean {
  if (startDate) {
    const start = new Date(startDate);
    if (new Date(transaction.date) < start) {
      return false;
    }
  }

  if (endDate) {
    const end = new Date(endDate);
    if (new Date(transaction.date) > end) {
      return false;
    }
  }

  return true;
}

/**
 * Check if transaction matches category filter
 */
function matchesCategory(
  transaction: Transaction,
  category?: string | string[]
): boolean {
  if (!category) return true;

  if (Array.isArray(category)) {
    // Empty array means no category filter
    if (category.length === 0) return true;
    return category.includes(transaction.category);
  }

  if (category === 'all') return true;
  return transaction.category === category;
}

/**
 * Check if transaction matches search term using fuzzy search
 */
function matchesSearch(transaction: Transaction, search?: string): boolean {
  if (!search) return true;

  const searchLower = search.toLowerCase();

  // Check if any field contains the search term (simple substring match first for speed)
  const description = (transaction.description || '').toLowerCase();
  const displayName = (transaction.displayName || '').toLowerCase();
  const memo = (transaction.memo || '').toLowerCase();

  // Simple substring match first (fast path)
  if (description.includes(searchLower) ||
      displayName.includes(searchLower) ||
      memo.includes(searchLower)) {
    return true;
  }

  // Fuzzy match as fallback for typos (only for longer searches to avoid false positives)
  if (search.length >= 5) {
    const fuse = new Fuse([transaction], {
      keys: ['description', 'displayName', 'memo'],
      threshold: 0.2, // Tighter threshold - closer to exact match
      ignoreLocation: true,
      includeScore: true
    });

    const results = fuse.search(search);
    return results.length > 0;
  }

  return false;
}

/**
 * Check if transaction matches amount range
 */
function matchesAmountRange(
  transaction: Transaction,
  minAmount?: number | string,
  maxAmount?: number | string
): boolean {
  // Use the actual amount (with sign) for filtering
  const amount = transaction.amount;

  if (minAmount !== undefined && minAmount !== '') {
    const min = typeof minAmount === 'string' ? parseFloat(minAmount) : minAmount;
    if (amount < min) return false;
  }

  if (maxAmount !== undefined && maxAmount !== '') {
    const max = typeof maxAmount === 'string' ? parseFloat(maxAmount) : maxAmount;
    if (amount > max) return false;
  }

  return true;
}

/**
 * Filter transactions by various criteria
 */
export function filterTransactions(
  transactions: Transaction[],
  filters: FilterCriteria = {}
): Transaction[] {
  console.log('filterTransactions called with', transactions.length, 'transactions');
  console.log('Filters:', filters);

  let filtered = [...transactions];

  // Apply date range filter
  if (filters.startDate || filters.endDate) {
    filtered = filtered.filter(t =>
      matchesDateRange(t, filters.startDate, filters.endDate)
    );
    console.log('After date filter:', filtered.length);
  }

  // Apply category filter
  if (filters.category) {
    filtered = filtered.filter(t => matchesCategory(t, filters.category));
    console.log('After category filter:', filtered.length);
  }

  // Apply person filter
  if (filters.person && filters.person !== 'all') {
    console.log('Filtering by person:', filters.person);
    filtered = filtered.filter(t => t.person === filters.person);
    console.log('After person filter:', filtered.length);
  }

  // Apply institution filter
  if (filters.institution && filters.institution !== 'all') {
    console.log('Filtering by institution:', filters.institution);
    filtered = filtered.filter(t => t.bank === filters.institution);
    console.log('After institution filter:', filtered.length);
  }

  // Apply search filter
  if (filters.search) {
    console.log('Filtering by search:', filters.search);
    filtered = filtered.filter(t => matchesSearch(t, filters.search));
    console.log('After search filter:', filtered.length);
  }

  // Apply amount range filter
  if (filters.minAmount !== undefined || filters.maxAmount !== undefined) {
    filtered = filtered.filter(t =>
      matchesAmountRange(t, filters.minAmount, filters.maxAmount)
    );
    console.log('After amount filter:', filtered.length);
  }

  // Apply excluded filter (default: hide excluded transactions)
  if (!filters.showExcluded) {
    console.log('Hiding excluded transactions');
    filtered = filtered.filter(t => !t.excluded);
    console.log('After excluded filter:', filtered.length);
  }

  console.log('Final filtered count:', filtered.length);
  return filtered;
}

// ============================================================================
// Sorting Functions
// ============================================================================

/**
 * Get comparable value for sorting
 */
function getSortValue(transaction: Transaction, sortBy: SortField): string | number {
  switch (sortBy) {
    case 'date':
      return new Date(transaction.date).getTime();
    case 'amount':
      return transaction.amount; // Use actual amount (not absolute) to sort negative to positive
    case 'description':
      return transaction.description.toLowerCase();
    case 'category':
      return transaction.category.toLowerCase();
    case 'person':
      return transaction.person.toLowerCase();
    default:
      return transaction.date;
  }
}

/**
 * Compare two values for sorting
 */
function compareValues(
  a: string | number,
  b: string | number,
  order: SortOrder
): number {
  if (a < b) return order === 'asc' ? -1 : 1;
  if (a > b) return order === 'asc' ? 1 : -1;
  return 0;
}

/**
 * Sort transactions
 */
export function sortTransactions(
  transactions: Transaction[],
  sortBy: SortField = 'date',
  sortOrder: SortOrder = 'desc'
): Transaction[] {
  const sorted = [...transactions];

  sorted.sort((a, b) => {
    const aVal = getSortValue(a, sortBy);
    const bVal = getSortValue(b, sortBy);
    return compareValues(aVal, bVal, sortOrder);
  });

  return sorted;
}

// ============================================================================
// Aggregation Functions
// ============================================================================

/**
 * Get top N categories by spending
 */
export function getTopCategories(
  transactions: Transaction[],
  limit: number = 5
): TopCategory[] {
  const summary = calculateSummary(transactions);
  const categories = Object.entries(summary.byCategory)
    .map(([name, data]) => ({ name, ...data }))
    .sort((a, b) => b.amount - a.amount)
    .slice(0, limit);

  return categories;
}

// ============================================================================
// Formatting Functions
// ============================================================================

/**
 * Format currency
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
}

/**
 * Format date
 */
export function formatDate(date: string | Date): string {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }).format(new Date(date));
}

/**
 * Calculate average monthly income from transactions
 * Excludes transfers and other non-income categories
 */
export function calculateAverageMonthlyIncome(
  transactions: Transaction[]
): number {
  if (transactions.length === 0) return 0;

  // Filter to only include recurring income categories
  // (Salary & Wages, Interest & Dividends)
  // Excludes one-time income like bonuses, windfalls, etc.
  const incomeTransactions = transactions.filter(t => {
    if (t.excluded) return false;
    if (t.amount <= 0) return false;
    const category = (t.category || '').toLowerCase();

    // Exclude transfers and similar categories from income calculation
    if (isExcludedCategory(category)) return false;

    // Only include recurring income categories
    const recurringIncomeCategories = [
      'salary & wages',
      'interest & dividends'
    ];

    return recurringIncomeCategories.includes(category);
  });

  if (incomeTransactions.length === 0) return 0;

  // Group by month and sum
  const incomeByMonth: Record<string, number> = {};
  incomeTransactions.forEach(t => {
    const monthKey = getMonthKey(t.date);
    if (!incomeByMonth[monthKey]) {
      incomeByMonth[monthKey] = 0;
    }
    incomeByMonth[monthKey] += t.amount;
  });

  // Get monthly income values
  const monthlyIncomes = Object.values(incomeByMonth).filter(income => income > 0);

  if (monthlyIncomes.length === 0) return 0;

  // Calculate average
  const totalIncome = monthlyIncomes.reduce((sum, income) => sum + income, 0);
  const avgIncome = totalIncome / monthlyIncomes.length;

  return Math.round(avgIncome); // Round to nearest dollar
}
